<template>
<div :class="['group-item', {'inactive': !element.active}]">
  <i class="sherpa-icon icon-copy item-icon"></i>
  <div class="question-description">
    <language-text
      :class="{'name-uppercased': !selected}"
      :id="element.id"
      :names="element.name"
      :getName="getName"
      :getLang="getLang"
      :selected="selected"
      :currentLanguage="currentLanguage"
      :active="element.active"
      :formBuilderContainer="formBuilderContainer"
      @languageTextPopupOpen="languageTextPopupOpen"
    />
  </div>
  <!-- Indicador de lista vacía -->
  <transition name="fade" mode="out-in">
    <div v-if="!element.children || element.children.length === 0 && !draggingElement"
      class="empty-list-indicator"
    >
      <span class="description">{{ getLang('dropItemsHere') }}</span>
      <i class="sherpa-icon icon-derecha"></i>
    </div>
    <draggable v-else
      :class="['list-group', 'group-draggable', {'dragging-element' : draggingElement}, {'selected-elements' : element.children && element.children.length > 0}, {'empty-elements' : !element.children || element.children.length == 0}]"
      v-model="element.children"
      item-key="id"
      v-bind="dragSelectedOptions"
      :disabled="disabledDraggable"
      @start="dragSelectedElementStart"
      @end="dragSelectedElementEnd"
      @add="elementAdded"
      @change="elementChanged"
    >
      <template #item="{ element: child }">
        <div
          :ref="'item-' + child.id"
          :key="child.id"
          :class="['list-group-item', child.type, {'conditional': isConditional(child)}, {'conditional-set': isConditionalSet(element, child)}]"
        >
          <group-item v-if="child.type === 'group'"
            :element="child"
            :parent="element"
            :getName="getName"
            :getTypeName="getTypeName"
            :getLang="getLang"
            :dragAllElement="dragAllElement"
            :dragSelectedElementQuestion="dragSelectedElementQuestion"
            :dragSelectedElementGroupId="dragSelectedElementGroupId"
            :parentGroupIds="[...parentGroupIds, element.id]"
            :dragSelectedExternalElementGroup="isExternalGroupDragged(child)"
            :dragSelectedOptions="dragSelectedOptions"
            :selected="selected"
            :deleteElementFromList="deleteElementFromList"
            :currentLanguage="currentLanguage"
            :formBuilderContainer="formBuilderContainer"
            :domainEndpoint="domainEndpoint"
            :formId="formId"
            :isConditional="isConditional"
            :getConditionalList="getConditionalList"
            :resetConditional="resetConditional"
            :deactivateSelectedListConditionals="deactivateSelectedListConditionals"
            :elementChangedConditionalDeactivate="elementChangedConditionalDeactivate"
            :conditionalSelected="conditionalSelected"
            :getElementFromSelectedList="getElementFromSelectedList"
            :resetConditionalValues="resetConditionalValues"
            @dragSelectedElementStart="dragSelectedChildElementStart"
            @dragSelectedElementEnd="dragSelectedChildElementEnd"
            @deleteElementCall="deleteChildElement"
            @languageTextPopupOpen="languageTextPopupOpen"
            @propagateActive="propagateActive"
            @newConditionalSelected="newConditionalSelected"
            @propagateInactive="propagateInactiveConditional"
          />
          <question-item v-else-if="child.type === 'question'"
            :element="child"
            :parent="element"
            :getName="getName"
            :getTypeName="getTypeName"
            :getLang="getLang"
            :selected="selected"
            :currentLanguage="currentLanguage"
            :formBuilderContainer="formBuilderContainer"
            :domainEndpoint="domainEndpoint"
            :formId="formId"
            :deactivateSelectedListConditionals="deactivateSelectedListConditionals"
            :conditionalSelected="conditionalSelected"
            @deleteElementCall="deleteChildElement"
            @languageTextPopupOpen="languageTextPopupOpen"
            @propagateActive="propagateActive"
            @propagateInactive="propagateInactiveConditional"
          />
          <transition name="fade" mode="out-in">
            <conditional-values v-if="isConditionalSet(element, child)"
              :ref="'conditional-' + child.id"
              :element="child"
              :parent="element"
              :getLang="getLang"
              :conditionalDomain="getElementFromSelectedList(element.conditional.id).domain"
              :currentLanguage="currentLanguage"
              @conditionalOptionsToggle="conditionalOptionsToggle"
            />
          </transition>
        </div>
      </template>
    </draggable>
  </transition>
  <template v-if="selected">
    <div class="edition-element">
      <transition-group name="fade-slide-left" type="transition">
        <template v-if="conditionalIdList.length > 0 && !isConditional(parent)">
          <template v-if="isConditional(element)">
            <div class="builder-select conditional-id" key="conditional-select">
              <multiselect
                :options="conditionalIdList"
                :show-labels="false"
                :searchable="false"
                :allow-empty="true"
                :disabled="!element.active"
                track-by="key"
                label="name"
                v-model="selectedConditionalId"
              />
            </div>
          </template>
          <toggle-button 
            key="conditional-button"
            v-model="element.conditional.active"
            :sync="true"
            :labels="{checked: getLang('conditional'), unchecked: getLang('notConditional')}"
            :width="135"
            :font-size="11"
            :margin="2"
            :speed="200"
            :color="{checked: '#bf830c', unchecked: '#cfcfcf', disabled: '#efefef'}"
            :disabled="!element.active"
            @change="conditionalChange"
          />
        </template>
      </transition-group>
      <toggle-button
        v-model="element.active"
        :sync="true"
        :labels="{checked: getLang('active'), unchecked: getLang('inactive')}"
        :width="78"
        :font-size="11"
        :margin="2"
        :speed="200"
        :color="{checked: '#4ab800c5', unchecked: '#cfcfcf', disabled: '#efefef'}"
        @change="activeChange"
      />
      <i class="sherpa-icon icon-trash" @click="deleteElementCall"></i>
    </div>
  </template>
</div>
</template>

<script>
import draggable from 'vuedraggable'
import groupItem from './groupItem'
import questionItem from './questionItem'
import languageText from './languageText'
import conditionalValues from './conditionalValues'
import ToggleButton from '@/components/shared/ToggleButton.vue'
import Multiselect from '@/components/shared/Multiselect.vue' // 'vue-multiselect ^2.1.6' component with TMD modifications

export default {
  name: 'group-item',
  components: {
    draggable,
    groupItem,
    questionItem,
    languageText,
    conditionalValues,
    ToggleButton,
    Multiselect
  },
  props: {
    element: Object,
    parent: Object,
    getName: Function,
    getTypeName: Function,
    getLang: Function,
    deleteElementFromList: Function,
    dragAllElement: Boolean,
    dragSelectedElementQuestion: Boolean,
    dragSelectedElementGroupId: String,
    parentGroupIds: Array,
    dragSelectedExternalElementGroup: Boolean,
    dragSelectedOptions: Object,
    selected: Boolean,
    currentLanguage: String,
    formBuilderContainer: HTMLElement,
    domainEndpoint: String,
    formId: String,
    isConditional: Function,
    getConditionalList: Function,
    resetConditional: Function,
    deactivateSelectedListConditionals: Function,
    elementChangedConditionalDeactivate: Function,
    conditionalSelected: Array,
    getElementFromSelectedList: Function,
    resetConditionalValues: Function
  },
  data () {
    return {
      dragSelectedElementGroup: false,
      disabledDraggable: false,
      conditionalOptionsOpen: true
    }
  },
  methods: {
    dragSelectedElementStart (event) {
      if (this.element.children[event.oldIndex].type === 'group') {
        this.dragSelectedElementGroup = true
      }
      this.$emit('dragSelectedElementStart', this.element.children[event.oldIndex], event)
    },
    dragSelectedElementEnd (event) {
      this.dragSelectedElementGroup = false
      this.$emit('dragSelectedElementEnd', this.element.children[event.newIndex])
    },
    dragSelectedChildElementStart (element, event) {
      this.$emit('dragSelectedElementStart', element, event)
      if (element.type === 'group') {
        this.dragSelectedElementGroup = true
      }
    },
    dragSelectedChildElementEnd (element) {
      this.dragSelectedElementGroup = false
      this.$emit('dragSelectedElementEnd', element)
    },
    deleteElementCall () {
      this.$emit('deleteElementCall', this.element)
    },
    deleteChildElement (childElement) {
      this.deleteElementFromList(childElement, this.element.children)
    },
    isExternalGroupDragged (child) {
      return this.dragSelectedElementGroupId != null && this.dragSelectedElementGroupId !== this.element.id && this.dragSelectedElementGroupId !== child.id && !this.parentGroupIds.includes(this.dragSelectedElementGroupId)
    },
    languageTextPopupOpen (open) {
      this.disabledDraggable = open
      this.$emit('languageTextPopupOpen', open)
    },
    activeChange (toggle) {
      if (toggle.value === false) {
        this.resetConditional(this.element)
        this.deactivateSelectedListConditionals(this.element, this.parent ? this.parent.children : null, false, null)
        this.propagateInactive(this.element.children)
        this.$emit('propagateInactive', this.element)
      } else {
        this.$emit('propagateActive', this.element)
      }
    },
    propagateInactive (elements) {
      if (elements) {
        elements.forEach((element) => {
          const ref = this.$refs['item-' + element.id]
          const container = Array.isArray(ref) ? ref[0] : ref
          if (!container) return
          container.classList.remove('conditional-options-close')
          element.active = false
          this.resetConditional(element)
          this.propagateInactive(element.children)
        })
      }
    },
    propagateActive (child) {
      if (!this.element.active) {
        this.element.active = true
        this.$emit('propagateActive', this.element)
      }
      if (this.isConditionalSet(this.element, child)) {
        child.conditionalValues = []
      }
    },
    propagateInactiveConditional (element) {
      element.conditionalValues = null
      this.conditionalContainerCloseClass(element.id, true)
    },
    elementAdded (event) {
      if (this.element.children[event.newIndex].active === true) {
        this.propagateActive(this.element.children[event.newIndex])
      }
    },
    elementChanged (event) {
      this.elementChangedConditionalDeactivate(event, this.element.children)
    },
    conditionalChange (toggle) {
      if (toggle.value && this.element.children) {
        this.element.children.forEach(element => {
          this.resetConditional(element)
        })
      } else {
        this.resetConditional(this.element)
      }
    },
    newConditionalSelected (conditional) {
      this.$emit('newConditionalSelected', conditional)
    },
    conditionalOptionsToggle (id, optionsOpen) {
      this.conditionalContainerCloseClass(id, !optionsOpen)
    },
    conditionalContainerCloseClass (id, add) {
      const ref = this.$refs['item-' + id]
      const container = Array.isArray(ref) ? ref[0] : ref
      if (!container) return
      if (add) {
        container.classList.add('conditional-options-close')
      } else {
        container.classList.remove('conditional-options-close')
      }
    },
    isConditionalSet (element, child) {
      return this.isConditional(element) && element.conditional.id && element.conditional.id !== '' && child.active
    },
    initializeConditionalValues (list) {
      if (list) {
        list.forEach(element => {
          if (element.conditionalValues === null) {
            element.conditionalValues = []
          }
        })
      }
    }
  },
  computed: {
    draggingElement () {
      return this.dragAllElement || this.dragSelectedElementQuestion || this.dragSelectedExternalElementGroup
    },
    selectedConditionalId: {
      get () {
        return this.conditionalIdList.find(option => this.element.conditional && (option.key === this.element.conditional.id)) || null
      },
      set (selected) {
        this.element.conditional.id = selected ? selected.key : null
      }
    },
    conditionalIdList () {
      return this.getConditionalList(this.element.id)
    }
  },
  watch: {
    selectedConditionalId: {
      handler (newVal, oldVal) {
        if (!newVal) {
          this.resetConditionalValues(this.element.children)
        } else {
          this.initializeConditionalValues(this.element.children)
        }
        this.$emit('newConditionalSelected', newVal)
      },
      deep: true
    }
  }
}
</script>

<style lang="scss" scoped>
.group-item {
  transition: all 0.2s ease;
  padding: 10px 12px 12px;

  &.inactive {
    opacity: 0.6;

    .question-item,
    .group-item {
      opacity: 1 !important;
    }
  }

  .children-container {
    margin-top: 10px;
    padding-left: 20px;
  }

  .group-draggable {
    position: relative;
    display: none;
    margin-top: 5px;
    padding: 5px 5px 5px 5px;

    & > span {
      position: relative;
      min-height: 107.5px;
      padding-right: 5px;
      margin-right: 10px;
    }
    &.dragging-element,
    &.selected-elements {
      display: block !important;
    }
    &.empty-elements {
      padding-bottom: 0;
    }

    .group.conditional-set {
      padding-bottom: 80px;

      &.conditional-options-close {
        padding-bottom: 47px;
      }
    }
    .question.conditional-set {
      padding-bottom: 70px;

      &.conditional-options-close {
        padding-bottom: 37px;
      }
    }
  }

  .edition-element {
    right: 15px;
    top: 10px;

    // transition-group renders as <span> — make it flex so children align inline
    > span {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      flex-shrink: 0;
    }

    .conditional-id {
      display: inline-flex;
      align-items: center;
      width: 220px;
      min-width: 0;

      .multiselect {
        height: 26px !important;
        min-height: 26px !important;
        width: 100%;

        .multiselect__select,
        .multiselect__tags {
          height: 26px !important;
          min-height: 26px !important;
        }

        .multiselect__select {
          padding: 0;
          height: 25px;
          min-height: 25px;
          line-height: 15px;
          width: 28px;
        }

        .multiselect__tags {
          padding: 4px 30px 0 8px;
          border-radius: 0;

          .multiselect__single {
            font-size: 13px;
            margin-bottom: 0;
            line-height: 18px;
          }
        }
      }
    }
  }
}
</style>
